/* eslint-disable camelcase */
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { export2Csv, parseRegionData, calcDewPoint } from "../../utils/weatherForecastUtils";
import { cropr, gettext, pgettext } from "../../adapter";
import moment from "moment";
import { ReactComponent as InformationIcon } from "./../../images/svg/information.svg";
import { ReactComponent as LoadingIcon } from "./../../images/svg/loader.svg";
import { ReactComponent as ExportIcon } from "./../../images/svg/export.svg";
import { ReactComponent as ArrowDownIcon } from "./../../images/svg/arrowDown.svg";
import { calcWinddir } from "../../utils/weatherStationUtils";
import DataGrid from "../shared/DataGrid";

const WeatherForecastTables = () => {
  const [exportOpen, setExportOpen] = useState(false);

  const regionState = useSelector((state) => {
    return state.weatherRegion;
  });

  const { region, isFetching } = useMemo(() => {
    if (!regionState.region) {
      return { ...regionState };
    } else {
      return {
        ...parseRegionData(regionState.regionData),
      };
    }
  }, [regionState]);

  let domain = [];
  if (region) {
    domain.push(region.firstDataPointDate);
    domain.push(region.lastDataPointDate);
  }

  var hcolumns = [
    { name: "date", title: gettext("Date") },
    { name: "time", title: gettext("Time") },
    {
      name: "at",
      title: pgettext("temperature abrreviation", "T") + " " + cropr.l10n.unit_temp_display,
      type: "numericColumn",
    },
    {
      name: "rh",
      title: pgettext("relative humidity abrreviation", "RH") + " %",
      type: "numericColumn",
    },
    {
      name: "rad",
      title: pgettext("radiation abrreviation", "Rad") + " J/cm\u00b2",
      type: "numericColumn",
    },
    {
      name: "prec",
      title:
        pgettext("Precipitation abbreviation", "Precip.") +
        " " +
        cropr.l10n.unit_precipitation_display +
        pgettext("per hour abbreviation", "/h"),
      type: "numericColumn",
    },
    {
      name: "pc",
      title: pgettext("Precipitation abbreviation", "Precip.") + " %",
      type: "numericColumn",
    },
    {
      name: "ws",
      title: gettext("Wind") + " " + cropr.l10n.unit_windspeed_display,
      type: "numericColumn",
    },
    { name: "wd", title: gettext("Wind") + " \u00b0", type: "rightAligned" },
    {
      name: "dp",
      title: gettext("Dewpoint") + " " + cropr.l10n.unit_temp_display,
      type: "numericColumn",
    },
  ];

  if (!region || !region.firstDataPointDate) return <div />;

  var preloaderClass = "object-preloader is-loaded";
  if (isFetching) {
    preloaderClass = "object-preloader is-loading";
  }

  var exportClass = "panel-options__item dropdown__wrapper";
  if (exportOpen) {
    exportClass = "panel-options__item dropdown__wrapper is-open";
  }

  var meas = region.forecast.raw;
  var dataSet = [];
  var d = new Date();
  var precipitation_precision = cropr.l10n.unit_precipitation === "in" ? 2 : 1;
  var offset = d.getTimezoneOffset() * 1000 * 60;
  for (var i = 0; i < meas.length; i++) {
    d = new Date(meas[i].timestamp * 1000 + offset);
    dataSet.push({
      id: i.toString(),
      date: moment(d).format("L"),
      time: moment(d).format("LT"),
      at: cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(meas[i].values.air_temperature), 1),
      rh: cropr.toLocaleFixed(meas[i].values.rel_humidity, 0),
      rad: cropr.toLocaleFixed(meas[i].values.radiation, 0),
      prec: cropr.toLocaleFixed(
        cropr.l10n_utils.mm_to_l10n(meas[i].values.precipitation),
        precipitation_precision
      ),
      pc: cropr.toLocaleFixed(meas[i].values.precip_chance, 0),
      ws: cropr.toLocaleFixed(cropr.l10n_utils.mps_to_l10n(meas[i].values.wind_speed), 1),
      wd: calcWinddir(meas[i].values.wind_direction),
      dp: cropr.toLocaleFixed(
        cropr.l10n_utils.celsius_to_l10n(
          calcDewPoint(meas[i].values.rel_humidity, meas[i].values.air_temperature)
        ),
        1
      ),
    });
  }

  return (
    <div>
      <div>
        <div className="l-full">
          <div className="panel has-object-preloader">
            <div className={preloaderClass}>
              <div className="object-preloader--content">
                <LoadingIcon className="loader" />
              </div>
            </div>

            <div className="panel--header">
              <h2>{region.name}</h2>

              <ul className="panel-options">
                <li
                  className={exportClass}
                  onClick={() => {
                    setExportOpen(!exportOpen);
                  }}
                >
                  {/* eslint-disable-next-line */}
                  <a className="dropdown--toggle">
                    <ExportIcon className="icon" /> {gettext("Export")}
                  </a>
                  <div className="dropdown">
                    <ul className="dropdown__list">
                      <li>
                        {/* eslint-disable-next-line */}
                        <a onClick={() => export2Csv(region)}>{gettext("To CSV")}</a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
              <div className="panel--settings">
                <div className="dropdown">
                  {/* eslint-disable-next-line */}
                  <a className="dropdown--toggle">
                    <ArrowDownIcon className="icon dropdown--arrow" />
                    {gettext("Export")}
                  </a>
                  <div className="dropdown--popout ">
                    <ul className="dropdown--list">
                      <li>
                        {/* eslint-disable-next-line */}
                        <a onClick={() => export2Csv(region)}>{gettext("To CSV")}</a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="panel--settings__item">
                  <div
                    id="information"
                    className="dropdown"
                    onClick={() => {
                      var info = document.querySelector("#information"); // Using a class instead, see note below.
                      info.classList.toggle("is-open");
                    }}
                  >
                    {/* eslint-disable-next-line */}
                    <a className="panel--settings__icon">
                      <InformationIcon className="icon" />
                    </a>
                    <div className="dropdown--popout is-visible dropdown--popout__moretop dropdown--popout__wide">
                      <div className="dropdown--popout__content">
                        <p>Ik ben de HTML content</p>
                        <h3>test</h3>
                        <p>Ik ben de HTML content</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <DataGrid
              rows={dataSet}
              columns={hcolumns.map((c) => ({ key: c.name, title: c.title, type: c.type }))}
              maxHeight={400}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherForecastTables;
